/**
 * Display Scale Utility
 * 
 * This utility ensures the website looks consistent regardless of display scaling.
 * It detects the actual screen dimensions (not the scaled viewport) and applies
 * data attributes to the HTML element for consistent styling.
 */

export const initDisplayScale = () => {
  const updateDisplayScale = () => {
    const html = document.documentElement;
    
    // Get actual screen dimensions (not affected by display scaling)
    const actualWidth = window.screen.width;
    
    // Get viewport dimensions (affected by display scaling)
    const viewportWidth = window.innerWidth;
    
    // Calculate device pixel ratio
    const devicePixelRatio = window.devicePixelRatio || 1;
    
    // Calculate effective scale factor
    // If viewport is smaller than screen, there's scaling applied
    const rawScaleFactor = actualWidth / viewportWidth;
    
    // Round to nearest common scale factor for CSS selector matching
    // Common scale factors: 1.0 (100%), 1.25 (125%), 1.5 (150%), 1.75 (175%), 2.0 (200%)
    const commonScales = [1.0, 1.25, 1.5, 1.75, 2.0, 2.25, 2.5];
    const scaleFactor = commonScales.reduce((prev, curr) => 
      Math.abs(curr - rawScaleFactor) < Math.abs(prev - rawScaleFactor) ? curr : prev
    );
    
    // Set data attributes based on actual screen dimensions
    // These will be used for consistent breakpoint detection
    html.setAttribute('data-actual-width', actualWidth.toString());
    html.setAttribute('data-viewport-width', viewportWidth.toString());
    html.setAttribute('data-scale-factor', scaleFactor.toFixed(2));
    html.setAttribute('data-device-pixel-ratio', devicePixelRatio.toString());
    
    // Apply breakpoint classes based on actual screen width (not viewport)
    // This ensures consistent styling regardless of OS-level display scaling
    // Remove existing breakpoint classes
    html.classList.remove(
      'screen-xs', 'screen-sm', 'screen-md', 'screen-tablet',
      'screen-lg', 'screen-xl', 'screen-2xl', 'screen-3xl', 'screen-4xl', 'screen-5xl'
    );
    
    // Add class based on actual screen width (not viewport)
    // This way, a 1920px screen always gets screen-5xl class, even if scaling makes viewport 1280px
    if (actualWidth >= 1920) {
      html.classList.add('screen-5xl');
    } else if (actualWidth >= 1536) {
      html.classList.add('screen-4xl');
    } else if (actualWidth >= 1440) {
      html.classList.add('screen-3xl');
    } else if (actualWidth >= 1280) {
      html.classList.add('screen-2xl');
    } else if (actualWidth >= 1024) {
      html.classList.add('screen-xl');
    } else if (actualWidth >= 768) {
      html.classList.add('screen-lg');
    } else if (actualWidth >= 640) {
      html.classList.add('screen-tablet');
    } else if (actualWidth >= 425) {
      html.classList.add('screen-md');
    } else if (actualWidth >= 375) {
      html.classList.add('screen-sm');
    } else {
      html.classList.add('screen-xs');
    }
    
    // Apply CSS custom property for scale factor compensation if needed
    // This can be used in CSS to adjust sizing based on actual vs viewport dimensions
    html.style.setProperty('--scale-factor', scaleFactor.toString());
    html.style.setProperty('--actual-width', `${actualWidth}px`);
    html.style.setProperty('--viewport-width', `${viewportWidth}px`);
  };
  
  // Initialize on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateDisplayScale);
  } else {
    updateDisplayScale();
  }
  
  // Update on resize (though actual screen size shouldn't change)
  window.addEventListener('resize', updateDisplayScale);
  
  // Update on orientation change (for mobile devices)
  window.addEventListener('orientationchange', () => {
    setTimeout(updateDisplayScale, 100);
  });
};

